import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, KeyRound, Fingerprint } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { entrarConHuella, huellaDisponible } from "@/lib/passkeys";
import { MfaCodeStep, MfaSetupStep } from "@/components/MfaSteps";

const inp = "w-full h-12 rounded-2xl bg-[#0F1A30] border border-white/10 px-4 pl-11 text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/60 transition-colors duration-200";

export default function MobileLogin() {
  const { login, applyToken, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [huellaOk, setHuellaOk] = useState(false);
  useEffect(() => { huellaDisponible().then(setHuellaOk); }, []);

  const conHuella = async () => {
    setBusy(true);
    try {
      await entrarConHuella();
      await refresh();
    } catch (err) {
      if (err?.name === "NotAllowedError") toast.error("Cancelaste la verificación");
      else toast.error(err?.response?.data?.detail || "No pudimos entrar con la huella. Usa tu contraseña.");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await login(email, password);
      if (r?.mfa_required) setMfa({ modo: "CODE", token: r.pending_token, email: r.email });
      else if (r?.mfa_setup_required) setMfa({ modo: "SETUP", token: r.setup_token, email: r.email });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo ingresar");
    } finally { setBusy(false); }
  };

  const google = () => {
    sessionStorage.setItem("aipos_post_login", "/movil");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/movil";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[100dvh] bg-[#050912] text-slate-100 relative overflow-hidden flex flex-col px-6 pt-[max(env(safe-area-inset-top),40px)] pb-10" data-testid="mobile-login">
      <div className="absolute -top-32 -left-24 w-80 h-80 rounded-full bg-cyan-500/20 blur-[90px]" />
      <div className="absolute bottom-0 -right-24 w-72 h-72 rounded-full bg-amber-400/10 blur-[90px]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative">
        <div className="flex items-center gap-2">
          <img src="/brand/aipos-logo.png" alt="" className="w-9 h-9" />
          <span className="text-xs font-extrabold tracking-[0.2em] text-cyan-300 px-2 py-0.5 rounded bg-cyan-950/60 border border-cyan-500/30">AI-POS</span>
        </div>
        <h1 className="mt-14 text-4xl font-bold font-display leading-[1.05] tracking-tight">
          Tu negocio en el bolsillo, con <span className="text-cyan-400">alertas inteligentes</span>.
        </h1>
        <p className="mt-4 text-sm text-slate-400">Ingresa con tu cuenta Ai-POS.</p>
      </motion.div>

      <div className="relative mt-10">
        {mfa ? (
          <div className="rounded-3xl bg-white p-5 text-slate-900" data-testid="mobile-mfa">
            {mfa.modo === "CODE"
              ? <MfaCodeStep pendingToken={mfa.token} email={mfa.email} onDone={(r) => applyToken(r.access_token, r.refresh_token)} onCancel={() => setMfa(null)} />
              : <MfaSetupStep setupToken={mfa.token} email={mfa.email} onDone={(r) => applyToken(r.access_token, r.refresh_token)} />}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-500 absolute left-4 top-4" />
              <input data-testid="mobile-login-email" type="email" required autoComplete="email" value={email}
                     onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.cl" className={inp} />
            </div>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-slate-500 absolute left-4 top-4" />
              <input data-testid="mobile-login-password" type="password" required autoComplete="current-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className={inp} />
            </div>
            <button data-testid="mobile-login-submit" disabled={busy}
                    className="w-full h-12 rounded-2xl bg-cyan-500 hover:bg-cyan-400 text-[#04121A] font-bold flex items-center justify-center gap-2 transition-colors duration-200 shadow-[0_10px_40px_rgba(6,182,212,0.35)]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Entrar <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={google} data-testid="mobile-login-google"
                    className="w-full h-12 rounded-2xl border border-white/10 text-slate-200 text-sm font-semibold hover:bg-white/5 transition-colors duration-200">
              Continuar con Google
            </button>
            {huellaOk && (
              <button type="button" onClick={conHuella} data-testid="mobile-login-huella" disabled={busy}
                      className="w-full h-12 rounded-2xl border border-indigo-400/40 bg-indigo-500/10 text-indigo-200 text-sm font-bold flex items-center justify-center gap-2 hover:bg-indigo-500/20 transition-colors duration-200">
                <Fingerprint className="w-4 h-4" /> Entrar con huella
              </button>
            )}
            <Link to="/recuperar-clave" data-testid="mobile-login-forgot" className="block text-center text-xs text-slate-400 pt-2">¿Olvidaste tu contraseña?</Link>
          </form>
        )}
      </div>
    </div>
  );
}
